import { useEffect, useMemo, useRef, useState } from 'react'
import { CATEGORIES, CATEGORY_NAME as NAME, COMMON_CATEGORIES } from '../lib/categories'
import { I } from './Icons'
import { Sheet } from './Sheet'

// Counter words for Shopify's names.
const SYN: Record<string, string> = { tee: 't-shirt', tees: 't-shirt', tshirt: 't-shirt', ls: 't-shirt', longsleeve: 't-shirt', denim: 'jeans', shades: 'sunglasses', glasses: 'sunglasses', eyewear: 'sunglasses', mules: 'slippers', slides: 'sandals', kicks: 'sneakers', crewneck: 'sweatshirt', knit: 'sweater', jacket: 'jacket', chain: 'chain', pendant: 'pendant', cardholder: 'card cases' }
const parentOf = (name: string) => name.split(' > ').slice(0, -1).join(' › ')

/**
 * Pick the piece's Shopify category by hand (Tim, 2 Oct): Shopify's own guess from the title is sometimes wrong
 * (a Chrome Hearts piece landed in Shoes › Heels), so the person at the counter sets it and the guess never stands.
 */
export function CategorySheet({ value, onPick, onClose }: { value: string; onPick: (id: string, name: string) => void; onClose: () => void }) {
  const [q, setQ] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const searching = q.trim() !== ''
  const list = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean).map((w) => SYN[w] ?? w)
    if (!words.length) return COMMON_CATEGORIES.map((id) => [id, NAME.get(id) ?? ''] as [string, string])
    const match = (ws: string[]) => CATEGORIES.filter(([, n]) => { const l = n.toLowerCase(); return ws.every((w) => l.includes(w)) })
    let hits = match(words)
    if (!hits.length && words.length > 1) hits = match(words.slice(-1))   // "leather jacket" → the jackets
    // Rank: the category's own name starts with the word, then contains it, then only the path does; the usual ones first in each.
    const common = new Set(COMMON_CATEGORIES)
    const rank = ([id, n]: [string, string]) => {
      const last = n.split(' > ').pop()!.toLowerCase()
      const tier = words.every((w) => new RegExp('(^|[^a-z])' + w.replace(/[^a-z0-9]/g, '\\$&')).test(last)) ? 0 : words.every((w) => last.includes(w)) ? 1 : 2
      return tier * 100 + (common.has(id) ? 0 : 50) + n.split(' > ').length
    }
    return hits.map((h) => [h, rank(h)] as const).sort((a, b) => a[1] - b[1]).map(([h]) => h).slice(0, 80)
  }, [q])
  return (
    <Sheet onClose={onClose} center picker label="Pick a category">
      <div className="sheet__title">Category</div>
      <div className="search" style={{ background: 'var(--page)' }}>{I.search()}<input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: tee, jeans, ring, trucker…" /></div>
      <div className="picker-list">
        {list.map(([id, n]) => (
          <button key={id} type="button" className={'row row--nav' + (id === value ? ' picker-sel' : '')} onClick={() => onPick(id, n)}>
            <span className="row__label" style={{ minWidth: 0 }}>{n.split(' > ').pop()}<span className="person__sub">{parentOf(n)}</span></span>
            {id === value && <span className="row__value set">✓</span>}
          </button>
        ))}
        {list.length === 0 && <div className="empty">No category matches “{q.trim()}”. Try a simpler word.</div>}
      </div>
      {!searching && <div className="hint" style={{ padding: '8px 4px 0' }}>The usual ones. Type to search every Shopify category.</div>}
    </Sheet>
  )
}
